function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ConfirmationModal, Typography } from 'seagull';
import { routes } from "../../constants";
import { useEventReporter, useGlobalContext, useIoContext, useStore, useStoreActions } from "../../contexts";
import { handleRequest, sendErrReport } from "../../infra/utils";
import { contactModel, proposalTemplateModel } from "../../models";
import { AgreementName } from "./AgreementName";
import { getCreateRelationshipError } from "./AgreementStudio.utils";
import { fromAgreementStudioCommon } from "./AgreementStudioCommon";
import AgreementStudioWithLegalTermsModal from "./AgreementStudioWithLegalTermsModal";
import { ClientPreview } from "../ClientPreview/ClientPreview";
const saveTemplateLoader = 'saveTemplate';
export function AgreementStudioTemplateMode(props) {
  var _props$template;

  const history = useHistory();
  const {
    api,
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const {
    reportButtonClick
  } = useEventReporter();
  const scrollToTopRef = useRef(null);
  const [templateName, setTemplateName] = useState((_props$template = props.template) === null || _props$template === void 0 ? void 0 : _props$template.title);
  const [templateNameInvalid, setTemplateNameInvalid] = useState(false);
  const [showTemplateNameInvalid, setShowTemplateNameInvalid] = useState(false);
  const {
    getNow
  } = useIoContext();
  const contacts = useStore(state => state.contacts);
  const [contactsMap, sampleContact] = useMemo(() => {
    var _contacts$data;

    const contactsData = (_contacts$data = contacts.data) !== null && _contacts$data !== void 0 ? _contacts$data : [];
    const contactsMap = {};
    let sampleContact = contactModel.makeSampleContact({
      createdAt: getNow(),
      vendorId: props.worksAt.id
    });

    for (let i = 0; i < contactsData.length; i++) {
      const contact = contactsData[i];

      if (contact.isSample) {
        sampleContact = contact;
      } else {
        contactsMap[contact.id] = contact;
      }
    }

    return [contactsMap, sampleContact];
  }, [contacts, props.worksAt.id]);

  const onSubmit = async data => {
    logBI(props.template != null ? 10493 : 10492);

    if (templateName == null || templateName === '' || templateNameInvalid) {
      setShowTemplateNameInvalid(true);
      if (scrollToTopRef.current != null) scrollToTopRef.current();
      return;
    }

    const template = fromAgreementStudioCommon(data, templateName);
    globalContext.startLoader(saveTemplateLoader);
    const [, err, hasError] = await handleRequest(props.template != null ? api.proposalTemplates.save(props.template.id, template) : api.proposalTemplates.create(template).then(() => {}));

    if (hasError) {
      sendErrReport('an error occurred while saving a template', err);
      const {
        text
      } = getCreateRelationshipError(err);
      globalContext.showErrorToast(text);
      globalContext.stopLoader(saveTemplateLoader);
      return;
    }

    history.push(routes.proposalTemplates);
    globalContext.showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
    globalContext.stopLoader(saveTemplateLoader);
  };

  const toCommonWithId = template => ({
    id: template.id,
    coverLetter: template.coverLetter,
    extraClause: template.extraClause,
    requirePaymentMethod: template.requirePaymentMethod,
    allowManualPayments: template.allowManualPayments,
    isExistingCustomer: template.isExistingCustomer,
    customNotificationText: template.customNotificationText,
    services: template.services,
    serviceBundles: template.serviceBundles,
    netTerms: template.netTerms,
    legalTerms: template.legalTerms,
    coverCreditCardFees: template.feeControls.coverCreditCardFees,

    /** Will automatic ongoing services be billed at the start or end of the billing-period. */
    amendmentsApprovalMethod: template.amendmentsApprovalMethod,
    requestAccountantAccess: template.requestAccountantAccess
  });

  const openPreviewModal = data => {
    const draft = proposalTemplateModel.toPreviewData(proposalTemplateModel.fromDTO(fromAgreementStudioCommon(data, templateName || '')));
    reportButtonClick('agreement-previewed-agreement');
    globalContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(ClientPreview, {
          type: "draft",
          draftInfo: {
            draft,
            sampleContact,
            contactsMap,
            legalTerms: props.legalTermsDocuments
          },
          dismissModal: dismissModal
        });
      }
    });
  };

  const onClose = async hasUnsavedChanges => {
    if (hasUnsavedChanges) {
      const discardChanges = await globalContext.showModal({
        content: function WarnUnsavedChangesModal(_ref2) {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/React.createElement(ConfirmationModal, {
            title: "Unsaved changes",
            skin: "danger",
            "data-testid": "warn-unsaved-changes-modal",
            content: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, null, "You have unsaved changes that will be lost if you leave this page. Are you sure you want to exit?")),
            primaryButtonLabel: "Discard changes",
            onPrimaryButtonClick: () => dismissModal(true),
            secondaryButtonLabel: "Keep editing",
            onSecondaryButtonClick: () => dismissModal(false),
            onCloseButtonClick: () => dismissModal(false)
          });
        }
      });
      return discardChanges;
    }

    return true;
  };

  return /*#__PURE__*/React.createElement(AgreementStudioWithLegalTermsModal, _extends({}, props, {
    relationshipDraft: props.template != null ? toCommonWithId(props.template) : null,
    modalTitle: props.template != null ? 'Edit proposal template' : 'Create proposal template',
    agreementTitleMP: /*#__PURE__*/React.createElement(AgreementName, {
      agreementTitle: templateName || '',
      setAgreementTitle: setTemplateName,
      setAgreementTitleValid: value => setTemplateNameInvalid(!value),
      showWarning: showTemplateNameInvalid,
      label: "Template name"
    }),
    doScrollToTopRef: scrollToTopRef,
    submitButtonLabel: props.template != null ? 'Save changes' : 'Save template',
    onSubmitCb: onSubmit,
    onCloseCb: onClose,
    openPreviewModal: openPreviewModal
  }));
}